'use client'

import { useEffect } from 'react'

import { PlusSquareOutlined24 } from '@vinted/monochrome-icons'
import { Badge, Cell, Icon, Image } from '@vinted/web-ui'

import { urlWithParams } from '@marketplace-web/browser/url-util'
import { StartBundleButtonWrapper } from '@marketplace-web/bundles/items-bundle-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  getSnadComplaint,
  transformComplaintDto,
} from '@marketplace-web/complaints/complaints-data'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  AdditionalTransactionAction,
  ApplicableTransactionAction,
  TransactionItemModel,
  TransactionSide,
  TransactionOrderModel,
  TransactionUserModel,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { conversationDetailsItemClick } from '@marketplace-web/messaging/tracking-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useUserProtectionVisibility } from '@marketplace-web/seller-protection/seller-protection-feature'
import { OrderDetailsItems } from '@marketplace-web/order-details/order-details-image-feature'

import { CREATE_BUNDLE_URL } from '../../../constants/routes'
import { isDateInTheFuture } from '../../../utils/date'

type Props = {
  transactionId: number
  conversationId: string
  complaintId: string | null
  transactionStatus?: number | null
  userSide: TransactionSide | null
  oppositeUserId?: string
  seller?: TransactionUserModel | null
  order: TransactionOrderModel
  availableActions: Array<AdditionalTransactionAction | ApplicableTransactionAction>
}

const ConversationOrderItems = ({
  transactionId,
  conversationId,
  complaintId,
  transactionStatus,
  userSide,
  order,
  seller,
  availableActions,
  oppositeUserId,
}: Props) => {
  const translate = useTranslate()
  const infoTranslate = useTranslate('conversation.conversation_info')
  const { track } = useTracking()
  const isSellerConversationDetailsItemImagesVisible = useUserProtectionVisibility({
    transactionStatus,
    isSellerBusiness: !!seller?.business,
    isCorrectUserSide: userSide === TransactionSide.Seller,
    abTestName: 'seller_conversation_details_item_images',
    shouldTrack: order.items.length > 0,
  })
  const isBuyerConversationDetailsItemImagesVisible = useUserProtectionVisibility({
    transactionStatus,
    isSellerBusiness: !!seller?.business,
    isCorrectUserSide: userSide === TransactionSide.Buyer,
    abTestName: 'buyer_conversation_details_item_images',
    shouldTrack: order.items.length > 0,
  })

  const isSellerCountdownActive = isDateInTheFuture(seller?.closetCountdownEndDate)

  const {
    fetch: fetchSnadComplaint,
    transformedData: complaint,
    isLoading,
  } = useFetch(getSnadComplaint, transformComplaintDto)

  useEffect(() => {
    if (!complaintId) return

    fetchSnadComplaint(transactionId)
  }, [complaintId, transactionId, fetchSnadComplaint])

  if (complaintId && isLoading) return <ContentLoader testId="conversation-info-loader" />

  const renderAvailableDiscount = () => {
    const availableDiscount = seller?.bundleDiscount

    if (!availableDiscount) return translate('bundles.discounts.not_available')

    const { discounts } = availableDiscount

    if (!discounts || !availableDiscount.isEnabled) {
      return translate('bundles.discounts.not_available')
    }

    const maxDiscount = Math.max(...discounts.map(element => Number(element.fraction))) * 100
    const maxItems = Math.max(...discounts.map(element => element.minimalItemCount))

    return translate('bundles.discounts.available', {
      availableDiscount: maxDiscount,
      count: maxItems,
    })
  }

  const renderAddMoreItemsAction = () => {
    if (!seller?.id || isSellerCountdownActive) return null

    const addMoreBundleItemsUrl = urlWithParams(CREATE_BUNDLE_URL(seller.id), {
      transaction_id: transactionId,
      item_ids: order.itemIds,
    })

    return (
      <StartBundleButtonWrapper url={addMoreBundleItemsUrl} screen="order_details">
        {({ onClick }) => (
          <Cell
            chevron
            title={infoTranslate('edit_order')}
            body={renderAvailableDiscount()}
            prefix={<Icon name={PlusSquareOutlined24} color="primary-default" />}
            type="navigating"
            onClick={onClick}
            testId="conversation-info-add-more-items-cell"
          />
        )}
      </StartBundleButtonWrapper>
    )
  }

  const renderOrderItemDetails = (item: TransactionItemModel) => {
    const complaintOrderItem = complaint?.complaintOrderItems?.find(
      complaintItem => complaintItem.item.id === item.id,
    )

    if (!complaintOrderItem?.reasonText) return null

    return <Badge content={complaintOrderItem.reasonText} theme="warning" styling="light" />
  }

  const handleOrderItemClick = () => {
    if (!transactionId || !conversationId) return

    track(
      conversationDetailsItemClick({
        transactionId,
        conversationId,
        userSide: userSide || null,
        oppositeUserId: oppositeUserId ?? null,
      }),
    )
  }

  if (isSellerConversationDetailsItemImagesVisible || isBuyerConversationDetailsItemImagesVisible) {
    return (
      <OrderDetailsItems
        orderItems={order.items || []}
        complaintOrderItems={complaint?.complaintOrderItems}
        userSide={userSide}
        transactionId={transactionId}
        onItemClick={handleOrderItemClick}
      />
    )
  }

  return (
    <>
      {order.items?.map(item => (
        <Cell
          key={item.id}
          type="navigating"
          chevron
          title={item.title}
          body={renderOrderItemDetails(item)}
          onClick={handleOrderItemClick}
          testId="conversation-item"
          prefix={<Image src={item.thumbnailUrl} size="large" styling="rounded" />}
          url={item.url}
          divider
        />
      ))}
      {availableActions?.includes(AdditionalTransactionAction.Bundle) && renderAddMoreItemsAction()}
    </>
  )
}

export default ConversationOrderItems
